/* eslint-disable react/no-danger */
import React from 'react';
import ReactDom from 'react-dom';
import _ from 'lodash';
import styles from './Modal.styl';
import {ModalArrow, CloseXIcon} from '../../Svgs';
import {
  bindMethods,
  handleAccessibleKeyDown,
} from '../../utilities/componentHelpers';

/**
 * Modal module
 */
export default class Modal extends React.Component {
  static propTypes = {
    children: React.PropTypes.node,
    className: React.PropTypes.string,
    classNameCloseBtn: React.PropTypes.string,
    classNameContent: React.PropTypes.string,
    classNameItem: React.PropTypes.string,
    contentLength: React.PropTypes.number,
    currentIndex: React.PropTypes.number,
    handleClose: React.PropTypes.func,
    handleSetContentIndex: React.PropTypes.func,
    isPhone: React.PropTypes.bool,
    resizeCallback: React.PropTypes.func,
    showCloseButton: React.PropTypes.bool,
  };

  static defaultProps = {
    showCloseButton: true,
  };

  /**
   * Modal Component constructor
   * @param  {Object} props   Passed in properties.
   * @return {Undefined}           Returns nothing.
   */
  constructor(props) {
    super(props);

    /**
     * Component state, not a Flux object.
     * @type {Object}
     */
    this.state = {};

    /**
     * Container height, used to set inline style for animation
     * @type {Number}
     */
    this.state.containerHeight = 0;

    /**
     * X Position of swipe touch
     * @type {Number}
     */
    this.touchStartX = 0;

    /**
     * Y Position of swipe touch
     * @type {Number}
     */
    this.touchStartY = 0;

    /**
     * check if swipe is avialable
     * @type {Bool}
     */
    this.isSwipe = false;

    /**
     * Binds these methods to an instance of this Modal
     * @method bindMethods
     * @returns {Undefined} Returns nothing
     */
    bindMethods(this, [
      'updateHeight',
      'updateIndex',
      'handleBackgroundClick',
      'handleFirstElementTab',
      'handleKeyDown',
      'handleLastElementTab',
      'handleResize',
      'handleCloseKeyDown',
      'handleTouchEnd',
      'handleTouchStart',
    ]);
  }

  /**
   * Lifecycle method
   * @return {Undefined} Returns nothing
   */
  componentDidMount() {
    const wait = 100;

    document.body.style.overflow = 'hidden';
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('orientationchange', this.handleResize);
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('click', this.handleBackgroundClick);
    setTimeout(this.handleResize, wait);

    this.refs.container.focus();
  }

  /**
   * Lifecycle method
   * @return {Undefined} Returns nothing
   */
  componentWillUnmount() {
    document.body.style.overflow = 'visible';
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleResize);
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('click', this.handleBackgroundClick);
  }

  /**
   * Determines if this modal shows the navigation
   * @return {Boolean} Whether or not to show navigation
   */
  hasNavigation() {
    return this.props.contentLength > 0;
  }

  /**
   * Updates height of container based on content
   * @return {Undefined} Returns nothing
   */
  updateHeight() {
    const container = ReactDom.findDOMNode(this.refs.container);

    if (this.props.resizeCallback) {
      this.props.resizeCallback();
    }

    if (container) {
      /* this will go to offsetHeight if clientHeight is 0 */
      const containerHeight = container.clientHeight || container.offsetHeight;

      this.setState({containerHeight});
    }
  }

  /**
   * Updates index for pagination
   * @param  {String} dir String representing direction
   * @return {Undefined} Returns nothing
   */
  updateIndex(dir) {
    const currentIndex = this.props.currentIndex;
    const wait = 10;
    let nextIndex = 0;

    if (dir === 'next') {
      nextIndex =
        currentIndex + 1 < this.props.contentLength ? currentIndex + 1 : 0;
    } else {
      nextIndex =
        currentIndex - 1 >= 0 ? currentIndex - 1 : this.props.contentLength - 1;
    }

    this.props.handleSetContentIndex(nextIndex, dir);
    setTimeout(this.updateHeight, wait);
    setTimeout(this.handleResize, wait * 2);
  }

  /**
   * Handles window clicking for closing the modal
   * @param  {Object} e Event Object
   * @return {Undefined} Returns nothing
   */
  handleBackgroundClick(e) {
    if (e.target === this.refs.modal) {
      this.props.handleClose();
    }
  }

  /**
   * Key event handler
   * @param  {e} e Key Event
   * @return {Undefined} Returns nothing
   */
  handleKeyDown(e) {
    const code = e.keyCode ? e.keyCode : e.which;
    const LEFT_ARROW = 37;
    const RIGHT_ARROW = 39;
    const ESC = 27;

    if (code === ESC) {
      this.props.handleClose();
    } else if (this.hasNavigation()) {
      if (code === RIGHT_ARROW) {
        this.updateIndex('next');
      } else if (code === LEFT_ARROW) {
        this.updateIndex('previous');
      }
    }
  }

  /**
   * Event handler for window resizing
   * @return {Undefined} Returns nothing
   */
  handleResize() {
    this.updateHeight();
  }

  /**
   * Checks touch end location for condolence swipe.
   * @param  {Object} event Touch event
   * @return {Undefined} Returns nothing
   */
  handleTouchEnd(event) {
    if (this.isSwipe) {
      const touchEndX = event.nativeEvent.changedTouches[0].clientX;
      const touchEndY = event.nativeEvent.changedTouches[0].clientY;
      const TOUCH_DISTANCE = 100;

      if (
        Math.abs(touchEndX - this.touchStartX) >= TOUCH_DISTANCE &&
        Math.abs(touchEndY - this.touchStartY) < TOUCH_DISTANCE
      ) {
        if (touchEndX > this.touchStartX) {
          this.updateIndex('previous');
        } else {
          this.updateIndex('next');
        }
        this.isSwipe = false;
      }
    }
  }

  /**
   * Checks touch start location for condolence swipe
   * @param  {Object} event Touch event
   * @return {Undefined} Returns nothing
   */
  handleTouchStart(event) {
    if (this.hasNavigation()) {
      this.touchStartX = event.nativeEvent.touches[0].clientX;
      this.touchStartY = event.nativeEvent.touches[0].clientY;
      this.isSwipe = true;
    }
  }

  /**
   * Handles tabbing forwards past the last element to prevent exiting the modal focus
   * @param  {Object}   e The Event object
   * @param  {Function} callback Callback to be fired if tab not pressed
   * @return {Undefined} Returns nothing
   */
  handleLastElementTab(e, callback) {
    const code = e.keyCode ? e.keyCode : e.which;
    const TAB = 9;

    if (code === TAB && !e.shiftKey) {
      e.preventDefault();
      this.refs.container.focus();
    } else if (callback) {
      return callback(e);
    }
  }

  /**
   * Handles Tabbing backwards from the first element to prevent exiting the modal focus
   * @param  {Object} e The Event object
   * @param {Boolean} hasNavigation If the modal has navigation
   * @return {Undefined} Returns nothing
   */
  handleFirstElementTab(e, hasNavigation) {
    const code = e.keyCode ? e.keyCode : e.which;
    const TAB = 9;

    if (code === TAB && e.shiftKey && this.refs.container === e.target) {
      e.preventDefault();
      if (hasNavigation) {
        this.refs.previous.focus();
      } else {
        this.refs.closeModal.focus();
      }
    }
  }

  /**
   * Gets the Close Button Handler
   * @param  {Boolean} hasNavigation If the modal has navigation
   * @param  {Function}  handler The close button handler
   * @return {Function} The function to handle closing the button
   */
  getCloseButtonHandler(hasNavigation, handler) {
    return hasNavigation
      ? handler
      : _.partial(this.handleLastElementTab, _, handler);
  }

  /**
   * Gets the styles object - height animation not needed for mobile
   * @param  {Number}  containerHeight The container height
   * @param  {Boolean} isPhone If on a phone
   * @return {Object} The Styles object
   */
  getStyles(containerHeight, isPhone) {
    const height = isPhone ? 'auto' : containerHeight;

    return {height};
  }

  /**
   * React Component render
   * High tab indexes are set for Viddler children
   * @return {JSX} Modal rendered
   */
  render() {
    const {
      className,
      classNameContent,
      classNameCloseBtn,
      classNameItem,
    } = this.props;
    let navigation = null;
    const hasNavigation = this.hasNavigation();

    if (hasNavigation) {
      const handleNextClick = _.partial(this.updateIndex, 'next');
      const handlePrevClick = _.partial(this.updateIndex, 'previous');

      navigation = (
        <div className={styles.ModalNavigation}>
          <div
            aria-label="Next"
            className={`${styles.ModalNavButton} ${styles.ModalNavNext}`}
            id="next"
            onClick={handleNextClick}
            onKeyDown={_.partial(handleAccessibleKeyDown, _, handleNextClick)}
            role="button"
            tabIndex="1001"
          >
            <ModalArrow />
          </div>
          <div
            aria-label="Previous"
            className={styles.ModalNavButton}
            id="previous"
            onClick={handlePrevClick}
            onKeyDown={_.partial(
              this.handleLastElementTab,
              _,
              _.partial(handleAccessibleKeyDown, _, handlePrevClick)
            )}
            ref="previous"
            role="button"
            tabIndex="1001"
          >
            <ModalArrow />
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className={styles.ModalBackground} />
        <div
          aria-label="Modal"
          className={`${styles.Modal}${className ? ` ${className}` : ''}`}
          onTouchEnd={this.handleTouchEnd}
          onTouchStart={this.handleTouchStart}
          ref="modal"
          role="dialog"
          tabIndex="-1"
        >
          <div
            className={`${styles.ModalContent}${
              classNameContent ? ` ${classNameContent}` : ''
            }`}
            role="document"
          >
            {this.props.showCloseButton && (
              <button
                aria-label="Close Modal"
                className={`${styles.closeButton}${
                  classNameCloseBtn ? ` ${classNameCloseBtn}` : ''
                }`}
                id="closeModal"
                onClick={this.props.handleClose}
                onKeyDown={this.getCloseButtonHandler(
                  hasNavigation,
                  _.partial(handleAccessibleKeyDown, _, this.props.handleClose)
                )}
                ref="closeModal"
                role="button"
                tabIndex="1001"
              >
                <CloseXIcon primaryColor={'#48565e'} />
              </button>
            )}
            <div
              className={`${styles.ModalNavItem}${
                classNameItem ? ` ${classNameItem}` : ''
              }`}
              style={this.getStyles(
                this.state.containerHeight,
                this.props.isPhone
              )}
            >
              <div
                aria-label="Modal Content Container"
                onKeyDown={_.partial(
                  this.handleFirstElementTab,
                  _,
                  hasNavigation
                )}
                ref="container"
                tabIndex="1000"
              >
                {_.isFunction(_.get(this.props.children, 'type', false))
                  ? React.cloneElement(this.props.children, {
                      callbacks: {
                        load: [this.handleResize],
                      },
                    })
                  : this.props.children}
              </div>
            </div>
          </div>
          {navigation}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/reactModules/shared/library/Modal/Modal.js